import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, User, Tag, Share2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { blogAPI } from '../utils/api';
import ReactMarkdown from 'react-markdown';

export const BlogDetailPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await blogAPI.getOne(id);
        setPost(response.data);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <section className="pt-24 pb-20 min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-slate-200 rounded w-3/4"></div>
            <div className="h-64 bg-slate-200 rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-5/6"></div>
              <div className="h-4 bg-slate-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="pt-24 pb-20 min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Post Not Found</h1>
          <p className="text-slate-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link to="/#blog">
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-24 pb-20 min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50" data-testid="blog-detail-page">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/#blog" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-8 group">
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Articles
        </Link>

        {/* Header */}
        <header className="mb-8">
          <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white mb-4">
            {post.category}
          </Badge>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>
                {new Date(post.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{post.read_time}</span>
            </div>
            {post.author && (
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
            )}
          </div>

          {/* Share Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            className="border-slate-300"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Article
          </Button>
        </header>

        {/* Featured Image */}
        <div className="relative aspect-video mb-10 rounded-2xl overflow-hidden shadow-xl">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200';
            }}
          />
        </div>

        {/* Excerpt */}
        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg mb-10">
          <p className="text-lg text-slate-700 italic leading-relaxed">
            {post.excerpt}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-slate max-w-none mb-12">
          {/* Check if content appears to be markdown or plain text */}
          {post.content.includes('#') || post.content.includes('**') || post.content.includes('- ') ? (
            <ReactMarkdown
              components={{
                h1: ({children}) => <h1 className="text-3xl font-bold text-slate-900 mt-8 mb-4">{children}</h1>,
                h2: ({children}) => <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">{children}</h2>,
                h3: ({children}) => <h3 className="text-xl font-bold text-slate-900 mt-6 mb-3">{children}</h3>,
                p: ({children}) => <p className="text-slate-700 leading-relaxed mb-4">{children}</p>,
                ul: ({children}) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
                li: ({children}) => <li className="text-slate-700">{children}</li>,
                strong: ({children}) => <strong className="font-bold text-slate-900">{children}</strong>,
                em: ({children}) => <em className="italic">{children}</em>,
                blockquote: ({children}) => (
                  <blockquote className="border-l-4 border-emerald-500 pl-4 my-4 italic text-slate-600">
                    {children}
                  </blockquote>
                ),
                img: ({src, alt}) => (
                  <figure className="my-8">
                    <img 
                      src={src} 
                      alt={alt} 
                      className="w-full rounded-lg shadow-md"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {alt && <figcaption className="text-center text-sm text-slate-500 mt-2">{alt}</figcaption>}
                  </figure>
                ),
                a: ({href, children}) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline">
                    {children}
                  </a>
                ),
                code: ({children}) => (
                  <code className="bg-slate-100 px-2 py-1 rounded text-sm">{children}</code>
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          ) : (
            // Plain text - split into paragraphs
            post.content.split('\n\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index} className="text-slate-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              )
            ))
          )}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex items-center gap-2 mb-8">
            <Tag className="w-4 h-4 text-slate-400" />
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-sm">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Author Card (if author info available) */}
        {post.author && (
          <Card className="border-2 border-emerald-200 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {post.author.charAt(0)}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{post.author}</h3>
                  <p className="text-sm text-slate-600">Move to Cornwall Contributor</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="border-t pt-8">
          <Link to="/#blog">
            <Button variant="outline" className="border-emerald-300 text-emerald-600 hover:bg-emerald-50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              View All Articles
            </Button>
          </Link>
        </div>
      </article>
    </section>
  );
};
